import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiSave, FiArrowLeft, FiSend, FiPlus, FiTrash2, FiUpload, FiChevronUp, FiChevronDown, FiX, FiCopy, FiCheck, FiEdit3, FiZap } from 'react-icons/fi';
import { CATEGORIES } from '../../data/researchStore';
import { apiGetResearchById, apiCreateResearch, apiUpdateResearch, apiUploadFile } from '../../api/researchApi';
import { generateAllCitations, CITATION_FORMATS } from '../../utils/citationGenerator';
import GoogleSignInGate from '../../components/GoogleSignInGate';
import UserBadge from '../../components/UserBadge';

const SECTION_TYPES = [
  { value: 'text', label: 'Text / Paragraph' },
  { value: 'code', label: 'Code Block' },
  { value: 'table', label: 'Data Table' },
  { value: 'figure', label: 'Figure / Image' },
  { value: 'chart', label: 'Interactive Chart' },
  { value: 'references', label: 'References' },
];

function emptySection(type = 'text') {
  const base = { id: `sec-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, title: '', type };
  if (type === 'code') return { ...base, content: '', language: 'python' };
  if (type === 'table') return { ...base, headers: ['Column 1', 'Column 2'], rows: [['', '']] };
  if (type === 'figure') return { ...base, caption: '', imageUrl: '' };
  if (type === 'chart') return { ...base, caption: '', chartConfig: { chartType: 'bar', data: [], series: [], colors: [...CHART_PALETTE], height: 320 } };
  if (type === 'references') return { ...base, content: [''] };
  return { ...base, content: '' };
}

const CHART_PALETTE = [
  '#0052cc', '#7c3aed', '#059669', '#f59e0b', '#ef4444',
  '#06b6d4', '#ec4899', '#8b5cf6', '#f97316', '#14b8a6',
];

const CHART_PRESETS = [
  { name: 'Default', colors: ['#0052cc', '#7c3aed', '#059669', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#8b5cf6', '#f97316', '#14b8a6'] },
  { name: 'Ocean', colors: ['#0077b6', '#00b4d8', '#90e0ef', '#03045e', '#023e8a', '#0096c7', '#48cae4', '#ade8f4', '#caf0f8', '#002855'] },
  { name: 'Sunset', colors: ['#ff6b6b', '#ee5a24', '#f0932b', '#ffbe76', '#ffeaa7', '#fd79a8', '#e17055', '#fab1a0', '#fdcb6e', '#e55039'] },
  { name: 'Forest', colors: ['#2d6a4f', '#40916c', '#52b788', '#74c69d', '#95d5b2', '#1b4332', '#b7e4c7', '#d8f3dc', '#081c15', '#344e41'] },
  { name: 'Neon', colors: ['#00f5d4', '#00bbf9', '#fee440', '#f15bb5', '#9b5de5', '#00c49a', '#fb5607', '#ff006e', '#3a86ff', '#8338ec'] },
  { name: 'Grayscale', colors: ['#1e293b', '#334155', '#475569', '#64748b', '#94a3b8', '#cbd5e1', '#e2e8f0', '#f1f5f9', '#0f172a', '#f8fafc'] },
];

export default function AdminEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);

  const [form, setForm] = useState({
    title: '',
    authors: '',
    abstract: '',
    category: CATEGORIES[0],
    tags: '',
    status: 'draft',
    pdfUrl: '',
    venue: 'Vector Labs Reports',
    doi: '',
    volume: '',
    issue: '',
    pages: '',
  });
  const [sections, setSections] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // details | content

  // Citation review state
  const [showCitationReview, setShowCitationReview] = useState(false);
  const [citations, setCitations] = useState({});
  const [editingCitation, setEditingCitation] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);
  const [pendingData, setPendingData] = useState(null);

  /* ─── Test Autofill ───────────────────────────────────────── */
  const handleTestAutofill = () => {
    setForm({
      title: 'Scalable Multi-Agent Orchestration for Enterprise Data Pipelines',
      authors: 'J. Alvarez, S. Chen, M. Rodriguez, K. Patel',
      abstract: 'We present a novel framework for orchestrating multiple AI agents in enterprise data pipeline environments. Our approach leverages hierarchical task decomposition and dynamic resource allocation to achieve 94% throughput improvement over traditional ETL systems. We evaluate our system across three Fortune 500 deployments, demonstrating consistent sub-second latency for complex multi-step transformations while maintaining data integrity guarantees. The framework introduces a self-healing mechanism that reduces manual intervention by 87% compared to existing solutions.',
      category: 'AI Automation Systems',
      tags: 'ai, automation, data pipelines, machine learning, enterprise',
      status: 'draft',
      pdfUrl: '',
      venue: 'Vector Labs Technical Reports',
      doi: '10.48550/arXiv.2602.14523',
      volume: '3',
      issue: '1',
      pages: '1-18',
    });
    setSections([
      {
        id: `sec-${Date.now()}-intro`,
        title: '1. Introduction',
        type: 'text',
        content: 'Modern enterprises process terabytes of heterogeneous data daily through complex pipelines that span multiple systems, formats, and compliance boundaries. Traditional Extract-Transform-Load (ETL) approaches rely on rigid, hand-coded rules that break when upstream data schemas evolve — a frequent occurrence in fast-moving organizations.\n\nWe propose a multi-agent architecture where specialized AI agents collaborate to handle ingestion, validation, transformation, and routing tasks autonomously. Each agent operates within a sandboxed environment with defined capabilities and resource constraints, communicating through a shared message bus with guaranteed delivery semantics.\n\nOur contributions are threefold:\n1. A hierarchical task decomposition algorithm that reduces complex pipeline specifications into atomic agent tasks\n2. A dynamic resource allocator that optimizes agent placement across available compute\n3. A self-healing error recovery protocol with formal correctness guarantees',
      },
      {
        id: `sec-${Date.now()}-arch`,
        title: '2. System Architecture',
        type: 'figure',
        caption: 'Figure 1: Multi-agent orchestration architecture. The Coordinator Agent decomposes tasks and assigns them to specialized Worker Agents via the Message Bus.',
        imageUrl: '',
        chartConfig: {
          chartType: 'radar',
          title: 'Agent Capability Profile',
          height: 340,
          data: [
            { name: 'Ingestion', agents: 8, throughput: 92 },
            { name: 'Validation', agents: 5, throughput: 88 },
            { name: 'Transform', agents: 12, throughput: 95 },
            { name: 'Routing', agents: 4, throughput: 97 },
            { name: 'Monitoring', agents: 3, throughput: 85 },
            { name: 'Recovery', agents: 6, throughput: 91 },
          ],
          series: [
            { dataKey: 'agents', label: 'Active Agents', color: '#0052cc' },
            { dataKey: 'throughput', label: 'Throughput %', color: '#7c3aed' },
          ],
        },
      },
      {
        id: `sec-${Date.now()}-method`,
        title: '3. Methodology',
        type: 'text',
        content: 'Our framework consists of three core subsystems: the Task Decomposition Engine (TDE), the Agent Resource Manager (ARM), and the Self-Healing Protocol (SHP).\n\nThe TDE receives high-level pipeline specifications in a declarative YAML format and produces a directed acyclic graph (DAG) of atomic tasks. Each node in the DAG is annotated with resource requirements, timeout constraints, and retry policies. The decomposition algorithm runs in O(n log n) time where n is the number of pipeline stages.\n\nThe ARM maintains a real-time inventory of available agents and their capabilities. When a new task arrives, ARM uses a modified Hungarian algorithm to find the optimal agent-task assignment that minimizes total expected latency while respecting resource constraints.\n\nThe SHP monitors agent health through heartbeat signals and output validation. When an agent fails or produces anomalous output, SHP initiates a three-phase recovery: (1) snapshot the agent state, (2) reassign the task to a healthy agent, (3) validate output consistency with upstream expectations.',
      },
      {
        id: `sec-${Date.now()}-code`,
        title: '3.1 Core Orchestration Loop',
        type: 'code',
        language: 'python',
        content: 'class AgentOrchestrator:\n    def __init__(self, config: OrchestratorConfig):\n        self.tde = TaskDecompositionEngine(config.dag_spec)\n        self.arm = AgentResourceManager(config.agents)\n        self.shp = SelfHealingProtocol(config.recovery)\n        self.bus = MessageBus(config.bus_endpoint)\n\n    async def execute_pipeline(self, pipeline_spec: dict) -> PipelineResult:\n        dag = self.tde.decompose(pipeline_spec)\n        assignments = self.arm.assign(dag.tasks)\n        \n        results = {}\n        for task in dag.topological_order():\n            agent = assignments[task.id]\n            try:\n                result = await agent.execute(task, timeout=task.timeout)\n                self.shp.validate(task, result)\n                results[task.id] = result\n            except AgentFailure as e:\n                results[task.id] = await self.shp.recover(task, e)\n        \n        return PipelineResult(results, dag.metadata)',
      },
      {
        id: `sec-${Date.now()}-perf-chart`,
        title: '4. Throughput Analysis',
        type: 'chart',
        caption: 'Figure 2: Records processed per second across different pipeline configurations. Our multi-agent framework consistently outperforms traditional ETL at all batch sizes.',
        chartConfig: {
          chartType: 'line',
          title: 'Throughput vs. Batch Size',
          height: 320,
          data: [
            { name: '1K', ours: 18200, etl: 8400, ragPipeline: 12100 },
            { name: '5K', ours: 22500, etl: 10200, ragPipeline: 14800 },
            { name: '10K', ours: 24100, etl: 11800, ragPipeline: 15200 },
            { name: '50K', ours: 23800, etl: 12400, ragPipeline: 14600 },
            { name: '100K', ours: 24200, etl: 11200, ragPipeline: 13900 },
            { name: '500K', ours: 23600, etl: 9800, ragPipeline: 12200 },
          ],
          series: [
            { dataKey: 'ours', label: 'VectorFlow (Ours)', color: '#0052cc' },
            { dataKey: 'etl', label: 'Traditional ETL', color: '#ef4444' },
            { dataKey: 'ragPipeline', label: 'RAG Pipeline', color: '#f59e0b' },
          ],
        },
      },
      {
        id: `sec-${Date.now()}-table`,
        title: '4.1 Benchmark Comparison',
        type: 'table',
        headers: ['Metric', 'Traditional ETL', 'Our Framework', 'Improvement'],
        rows: [
          ['Throughput (records/sec)', '12,400', '24,100', '+94.4%'],
          ['P99 Latency (ms)', '2,340', '890', '-62.0%'],
          ['Manual Interventions/week', '47', '6', '-87.2%'],
          ['Schema Change Recovery (min)', '45', '3.2', '-92.9%'],
          ['Pipeline Uptime (%)', '97.1', '99.8', '+2.8pp'],
        ],
      },
      {
        id: `sec-${Date.now()}-latency-chart`,
        title: '4.2 Latency Distribution',
        type: 'chart',
        caption: 'Figure 3: P50/P95/P99 latency distribution across different operation types. Self-healing recovery adds minimal latency overhead.',
        chartConfig: {
          chartType: 'grouped-bar',
          title: 'Latency by Operation Type (ms)',
          height: 300,
          data: [
            { name: 'Ingest', p50: 12, p95: 45, p99: 120 },
            { name: 'Validate', p50: 8, p95: 28, p99: 65 },
            { name: 'Transform', p50: 34, p95: 145, p99: 340 },
            { name: 'Route', p50: 5, p95: 18, p99: 42 },
            { name: 'Recovery', p50: 89, p95: 320, p99: 890 },
          ],
          series: [
            { dataKey: 'p50', label: 'P50', color: '#059669' },
            { dataKey: 'p95', label: 'P95', color: '#f59e0b' },
            { dataKey: 'p99', label: 'P99', color: '#ef4444' },
          ],
        },
      },
      {
        id: `sec-${Date.now()}-cost-chart`,
        title: '4.3 Cost Analysis',
        type: 'chart',
        caption: 'Figure 4: Monthly operational cost breakdown showing 62% reduction compared to traditional approach.',
        chartConfig: {
          chartType: 'stacked-bar',
          title: 'Monthly Cost Comparison ($)',
          height: 300,
          data: [
            { name: 'Traditional', compute: 8200, storage: 3400, api: 0, monitoring: 1200 },
            { name: 'RAG Pipeline', compute: 4800, storage: 2100, api: 5600, monitoring: 900 },
            { name: 'Ours', compute: 3200, storage: 1800, api: 1400, monitoring: 600 },
          ],
          series: [
            { dataKey: 'compute', label: 'Compute', color: '#0052cc' },
            { dataKey: 'storage', label: 'Storage', color: '#7c3aed' },
            { dataKey: 'api', label: 'API Calls', color: '#f59e0b' },
            { dataKey: 'monitoring', label: 'Monitoring', color: '#059669' },
          ],
        },
      },
      {
        id: `sec-${Date.now()}-accuracy-chart`,
        title: '4.4 Accuracy Over Deployment',
        type: 'chart',
        caption: 'Figure 5: System accuracy improves over time as the feedback loop refines agent behavior. Error rate drops below 1% after week 3.',
        chartConfig: {
          chartType: 'area',
          title: 'Accuracy & Error Rate Over 8 Weeks',
          height: 300,
          data: [
            { name: 'Week 1', accuracy: 91.2, errorRate: 8.8 },
            { name: 'Week 2', accuracy: 93.8, errorRate: 6.2 },
            { name: 'Week 3', accuracy: 96.1, errorRate: 3.9 },
            { name: 'Week 4', accuracy: 97.4, errorRate: 2.6 },
            { name: 'Week 5', accuracy: 98.0, errorRate: 2.0 },
            { name: 'Week 6', accuracy: 98.3, errorRate: 1.7 },
            { name: 'Week 7', accuracy: 98.6, errorRate: 1.4 },
            { name: 'Week 8', accuracy: 98.9, errorRate: 1.1 },
          ],
          series: [
            { dataKey: 'accuracy', label: 'Accuracy %', color: '#059669' },
            { dataKey: 'errorRate', label: 'Error Rate %', color: '#ef4444' },
          ],
        },
      },
      {
        id: `sec-${Date.now()}-discussion`,
        title: '5. Discussion',
        type: 'text',
        content: 'The results validate our core hypothesis: production AI systems benefit significantly from modular, observable architectures that treat reliability as a first-class concern.\n\nSeveral insights emerged from our deployment experience:\n\n5.1 Error Handling Is Critical\nThe most impactful investment was in robust error handling and graceful degradation. Production LLM systems encounter novel failure modes that rule-based fallbacks handle poorly. Our circuit-breaker pattern with intelligent retries reduced unrecoverable errors by 94%.\n\n5.2 Monitoring Drives Improvement\nComprehensive observability — tracking not just latency and errors, but semantic quality metrics — enabled rapid iteration. Teams that deployed our monitoring stack improved their system quality 3x faster than those relying on manual review.',
      },
      {
        id: `sec-${Date.now()}-refs`,
        title: 'References',
        type: 'references',
        content: [
          'Yao, S., Zhao, J., Yu, D., et al. (2023). ReAct: Synergizing Reasoning and Acting in Language Models. ICLR 2023.',
          'Chase, H. (2023). LangChain: Building Applications with LLMs Through Composability. GitHub.',
          'Zaharia, M., et al. (2018). Accelerating the Machine Learning Lifecycle with MLflow. IEEE Data Eng. Bull.',
          'Dean, J., & Ghemawat, S. (2008). MapReduce: Simplified Data Processing on Large Clusters. CACM, 51(1), 107-113.',
          'Alvarez, J., & Chen, S. (2025). Hierarchical Agent Coordination in Distributed Systems. Vector Labs Technical Reports, 2(4), 1-12.',
        ],
      },
    ]);
    setActiveTab('content');
  };

  useEffect(() => {
    if (isEditing) {
      apiGetResearchById(id).then((res) => {
        if (res.ok && res.data) {
          const paper = res.data;
          setForm({
            title: paper.title,
            authors: Array.isArray(paper.authors) ? paper.authors.join(', ') : paper.authors,
            abstract: paper.abstract,
            category: paper.category,
            tags: Array.isArray(paper.tags) ? paper.tags.join(', ') : paper.tags,
            status: paper.status,
            pdfUrl: paper.pdfUrl || '',
            venue: paper.venue || 'Vector Labs Reports',
            doi: paper.doi || '',
            volume: paper.volume || '',
            issue: paper.issue || '',
            pages: paper.pages || '',
          });
          if (paper.citations) setCitations(paper.citations);
          if (paper.sections) setSections(paper.sections);
        }
      });
    }
  }, [id, isEditing]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const addSection = (type = 'text') => {
    setSections([...sections, emptySection(type)]);
  };

  const updateSection = (index, updates) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], ...updates };
    setSections(updated);
  };

  const removeSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const moveSection = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= sections.length) return;
    const updated = [...sections];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setSections(updated);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      try {
        const result = await apiUploadFile(file);
        if (result.ok) {
          setForm({ ...form, pdfUrl: result.data.url || result.data.key });
        } else {
          // Fallback to object URL if upload fails
          const url = URL.createObjectURL(file);
          setForm({ ...form, pdfUrl: url });
        }
      } catch {
        const url = URL.createObjectURL(file);
        setForm({ ...form, pdfUrl: url });
      }
    }
  };

  const buildData = (publishNow = false) => ({
    title: form.title.trim(),
    authors: form.authors.split(',').map((a) => a.trim()).filter(Boolean),
    abstract: form.abstract.trim(),
    category: form.category,
    tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
    status: publishNow ? 'published' : form.status,
    pdfUrl: form.pdfUrl.trim() || '#',
    venue: form.venue.trim(),
    doi: form.doi.trim() || undefined,
    volume: form.volume.trim() || undefined,
    issue: form.issue.trim() || undefined,
    pages: form.pages.trim() || undefined,
    sections: sections.length > 0 ? sections : undefined,
  });

  // Save as draft — skip citation review
  const handleSaveDraft = async (e) => {
    e.preventDefault();
    setSaving(true);
    const data = buildData(false);
    try {
      if (isEditing) await apiUpdateResearch(id, data);
      else await apiCreateResearch(data);
    } catch (err) { console.error('Save failed:', err); }
    setSaving(false);
    navigate('/admin');
  };

  // Publish — open citation review first
  const handlePublish = (e) => {
    e.preventDefault();
    const data = buildData(true);
    setPendingData(data);
    // Auto-generate citations from paper metadata
    const generated = generateAllCitations({
      ...data,
      publishedDate: new Date().toISOString().split('T')[0],
    });
    setCitations(generated);
    setShowCitationReview(true);
  };

  // Confirm citations and publish
  const handleConfirmPublish = async () => {
    setSaving(true);
    const data = {
      ...pendingData,
      citations,
      publishedDate: pendingData.publishedDate || new Date().toISOString().split('T')[0],
    };
    try {
      if (isEditing) await apiUpdateResearch(id, data);
      else await apiCreateResearch(data);
    } catch (err) { console.error('Save failed:', err); }
    setSaving(false);
    setShowCitationReview(false);
    navigate('/admin');
  };

  const handleCopyCitation = (key) => {
    navigator.clipboard.writeText(citations[key] || '');
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  return (
    <GoogleSignInGate>
      <div className="admin-page">
        <div className="admin-header">
          <div className="admin-header-inner">
            <Link to="/" className="research-brand">
              <span className="research-brand-text">
                <strong>Vector Labs</strong> Admin
              </span>
            </Link>
            <div style={{ flex: 1 }} />
            <UserBadge />
          </div>
        </div>

        <div className="admin-body">
          <Link to="/admin" className="rd-back" style={{ marginBottom: 24, display: 'inline-flex' }}>
            <FiArrowLeft /> Back to Dashboard
          </Link>

          <div className="editor-card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
              <h1 className="editor-title" style={{ margin: 0 }}>{isEditing ? 'Edit Publication' : 'New Publication'}</h1>
              {!isEditing && (
                <button className="admin-btn" onClick={handleTestAutofill}
                  style={{ background: '#7c3aed', color: '#fff', border: 'none' }}>
                  <FiZap /> Test Fill
                </button>
              )}
            </div>

            {/* Tab switcher */}
            <div className="editor-tabs">
              <button
                className={`editor-tab ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                Paper Details
              </button>
              <button
                className={`editor-tab ${activeTab === 'content' ? 'active' : ''}`}
                onClick={() => setActiveTab('content')}
              >
                Paper Content ({sections.length} sections)
              </button>
            </div>

            <form onSubmit={handleSaveDraft}>
              {/* Details tab */}
              {activeTab === 'details' && (
                <>
                  <div className="form-group">
                    <label htmlFor="title">Title</label>
                    <input
                      id="title"
                      name="title"
                      type="text"
                      value={form.title}
                      onChange={handleChange}
                      placeholder="e.g., Agent Workflow Orchestration: Patterns for Multi-Step LLM Pipelines"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="authors">Authors (comma-separated)</label>
                      <input
                        id="authors"
                        name="authors"
                        type="text"
                        value={form.authors}
                        onChange={handleChange}
                        placeholder="D. Mitchell, R. Vasquez"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="category">Category</label>
                      <select id="category" name="category" value={form.category} onChange={handleChange}>
                        {CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="abstract">Abstract</label>
                    <textarea
                      id="abstract"
                      name="abstract"
                      rows={8}
                      value={form.abstract}
                      onChange={handleChange}
                      placeholder="Write the abstract for this publication…"
                      required
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="tags">Tags (comma-separated)</label>
                      <input
                        id="tags"
                        name="tags"
                        type="text"
                        value={form.tags}
                        onChange={handleChange}
                        placeholder="agents, workflow, LLM"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="venue">Venue / Journal</label>
                      <input
                        id="venue"
                        name="venue"
                        type="text"
                        value={form.venue}
                        onChange={handleChange}
                        placeholder="Vector Labs Reports"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="doi">DOI (optional)</label>
                      <input
                        id="doi"
                        name="doi"
                        type="text"
                        value={form.doi}
                        onChange={handleChange}
                        placeholder="10.1234/example.2026"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="volume">Volume</label>
                      <input
                        id="volume"
                        name="volume"
                        type="text"
                        value={form.volume}
                        onChange={handleChange}
                        placeholder="e.g., 71"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="issue">Issue</label>
                      <input
                        id="issue"
                        name="issue"
                        type="text"
                        value={form.issue}
                        onChange={handleChange}
                        placeholder="e.g., 1"
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="pages">Pages</label>
                      <input
                        id="pages"
                        name="pages"
                        type="text"
                        value={form.pages}
                        onChange={handleChange}
                        placeholder="e.g., 379-388"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>PDF Upload</label>
                      <div className="pdf-upload-area">
                        <input
                          type="file"
                          accept="application/pdf"
                          onChange={handlePdfUpload}
                          id="pdf-upload"
                          style={{ display: 'none' }}
                        />
                        <label htmlFor="pdf-upload" className="pdf-upload-label">
                          <FiUpload /> {pdfFile ? pdfFile.name : 'Choose PDF file…'}
                        </label>
                        {form.pdfUrl && form.pdfUrl !== '#' && (
                          <span className="pdf-uploaded-badge">PDF attached</span>
                        )}
                      </div>
                    </div>
                    <div className="form-group">
                      <label htmlFor="status">Status</label>
                      <select id="status" name="status" value={form.status} onChange={handleChange}>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              {/* Content tab — rich sections editor */}
              {activeTab === 'content' && (
                <div className="sections-editor">
                  <p className="sections-editor-hint">
                    Build your paper content with multiple section types. Sections will display in the visual
                    paper reader. If no sections are added, a default layout is generated from the abstract.
                  </p>

                  {sections.map((section, i) => (
                    <div className="section-block" key={section.id}>
                      <div className="section-block-header">
                        <span className="section-block-type">{SECTION_TYPES.find(t => t.value === section.type)?.label}</span>
                        <div className="section-block-controls">
                          <button type="button" onClick={() => moveSection(i, -1)} disabled={i === 0}><FiChevronUp /></button>
                          <button type="button" onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1}><FiChevronDown /></button>
                          <button type="button" onClick={() => removeSection(i)} className="section-remove"><FiTrash2 /></button>
                        </div>
                      </div>

                      <div className="section-block-body">
                        <input
                          type="text"
                          placeholder="Section title (e.g., 1. Introduction)"
                          value={section.title}
                          onChange={(e) => updateSection(i, { title: e.target.value })}
                          className="section-title-input"
                        />

                        {section.type === 'text' && (
                          <textarea
                            rows={6}
                            placeholder="Write section content… Use • for bullet points. Use 1.1 style headings for subsections."
                            value={section.content}
                            onChange={(e) => updateSection(i, { content: e.target.value })}
                          />
                        )}

                        {section.type === 'code' && (
                          <>
                            <select
                              value={section.language || 'python'}
                              onChange={(e) => updateSection(i, { language: e.target.value })}
                              className="section-lang-select"
                            >
                              <option value="python">Python</option>
                              <option value="javascript">JavaScript</option>
                              <option value="typescript">TypeScript</option>
                              <option value="bash">Bash</option>
                              <option value="sql">SQL</option>
                              <option value="json">JSON</option>
                              <option value="yaml">YAML</option>
                            </select>
                            <textarea
                              rows={10}
                              placeholder="Paste code here…"
                              value={section.content}
                              onChange={(e) => updateSection(i, { content: e.target.value })}
                              className="section-code-textarea"
                            />
                          </>
                        )}

                        {section.type === 'figure' && (
                          <>
                            <input
                              type="text"
                              placeholder="Image URL"
                              value={section.imageUrl || ''}
                              onChange={(e) => updateSection(i, { imageUrl: e.target.value })}
                            />
                            <input
                              type="text"
                              placeholder="Figure caption"
                              value={section.caption || ''}
                              onChange={(e) => updateSection(i, { caption: e.target.value })}
                            />
                          </>
                        )}

                        {section.type === 'chart' && (
                          <>
                            <div className="chart-editor-row">
                              <select
                                value={section.chartConfig?.chartType || 'bar'}
                                onChange={(e) => updateSection(i, { chartConfig: { ...section.chartConfig, chartType: e.target.value } })}
                                className="section-lang-select"
                              >
                                <option value="bar">Bar Chart</option>
                                <option value="horizontal-bar">Horizontal Bar</option>
                                <option value="line">Line Chart</option>
                                <option value="area">Area Chart</option>
                                <option value="pie">Pie / Donut</option>
                                <option value="grouped-bar">Grouped Bar</option>
                                <option value="stacked-bar">Stacked Bar</option>
                                <option value="scatter">Scatter Plot</option>
                                <option value="radar">Radar Chart</option>
                              </select>
                            </div>
                            <div className="chart-color-picker">
                              <label className="chart-color-label">Series Colors</label>
                              <div className="chart-color-swatches">
                                {(section.chartConfig?.colors || CHART_PALETTE).map((c, ci) => (
                                  <div key={ci} className="chart-color-swatch-wrap">
                                    <input
                                      type="color"
                                      value={c}
                                      className="chart-color-input"
                                      onChange={(e) => {
                                        const newColors = [...(section.chartConfig?.colors || CHART_PALETTE)];
                                        newColors[ci] = e.target.value;
                                        updateSection(i, { chartConfig: { ...section.chartConfig, colors: newColors } });
                                      }}
                                    />
                                    <span className="chart-color-hex">{c}</span>
                                  </div>
                                ))}
                                <button
                                  className="chart-color-add"
                                  onClick={() => {
                                    const newColors = [...(section.chartConfig?.colors || CHART_PALETTE), '#64748b'];
                                    updateSection(i, { chartConfig: { ...section.chartConfig, colors: newColors } });
                                  }}
                                  title="Add color"
                                >+</button>
                              </div>
                              <div className="chart-preset-palettes">
                                <span>Presets:</span>
                                {CHART_PRESETS.map((preset, pi) => (
                                  <button key={pi} className="chart-preset-btn" title={preset.name}
                                    onClick={() => updateSection(i, { chartConfig: { ...section.chartConfig, colors: [...preset.colors] } })}>
                                    {preset.colors.slice(0, 5).map((clr, k) => (
                                      <span key={k} className="chart-preset-dot" style={{ background: clr }} />
                                    ))}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <textarea
                              rows={8}
                              placeholder='Chart data as JSON array, e.g.:\n[{"name": "A", "value": 42}, {"name": "B", "value": 85}]'
                              value={section.chartConfig?.data ? JSON.stringify(section.chartConfig.data, null, 2) : ''}
                              onChange={(e) => {
                                try {
                                  const parsed = JSON.parse(e.target.value);
                                  updateSection(i, { chartConfig: { ...section.chartConfig, data: parsed } });
                                } catch { /* let user keep typing */ }
                              }}
                              className="section-code-textarea"
                            />
                            <input
                              type="text"
                              placeholder="Chart caption"
                              value={section.caption || ''}
                              onChange={(e) => updateSection(i, { caption: e.target.value })}
                            />
                          </>
                        )}

                        {section.type === 'table' && (
                          <p className="section-table-note">
                            Table content is auto-generated in the reader preview.
                            Edit headers and rows via the JSON data store for full control.
                          </p>
                        )}

                        {section.type === 'references' && (
                          <textarea
                            rows={6}
                            placeholder="One reference per line…"
                            value={Array.isArray(section.content) ? section.content.join('\n') : section.content}
                            onChange={(e) => updateSection(i, { content: e.target.value.split('\n') })}
                          />
                        )}
                      </div>
                    </div>
                  ))}

                  <div className="add-section-bar">
                    {SECTION_TYPES.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        className="add-section-btn"
                        onClick={() => addSection(t.value)}
                      >
                        <FiPlus /> {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="admin-btn secondary" disabled={saving}>
                  <FiSave /> {saving ? 'Saving…' : 'Save as Draft'}
                </button>
                <button
                  type="button"
                  className="admin-btn primary"
                  disabled={saving}
                  onClick={handlePublish}
                >
                  <FiSend /> {saving ? 'Publishing…' : 'Save & Publish'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* ─── Citation Review Modal ───────────────────────── */}
        {showCitationReview && (
          <div className="citation-modal-overlay" onClick={() => setShowCitationReview(false)}>
            <div className="citation-modal" onClick={(e) => e.stopPropagation()}>
              <div className="citation-modal-header">
                <button className="citation-modal-close" onClick={() => setShowCitationReview(false)}>
                  <FiX />
                </button>
                <h2>Review Auto-Generated Citations</h2>
                <p className="citation-modal-subtitle">
                  Citations were generated from paper metadata. Review each format for accuracy, edit if needed, then confirm to publish.
                </p>
              </div>

              <div className="citation-modal-body">
                {CITATION_FORMATS.map(({ key, label }) => (
                  <div className="citation-format-block" key={key}>
                    <div className="citation-format-header">
                      <span className="citation-format-label">{label}</span>
                      <div className="citation-format-actions">
                        <button
                          className="citation-action-btn"
                          title={editingCitation === key ? 'Done editing' : 'Edit'}
                          onClick={() => setEditingCitation(editingCitation === key ? null : key)}
                        >
                          {editingCitation === key ? <FiCheck /> : <FiEdit3 />}
                        </button>
                        <button
                          className="citation-action-btn"
                          title="Copy"
                          onClick={() => handleCopyCitation(key)}
                        >
                          {copiedKey === key ? <FiCheck style={{ color: '#059669' }} /> : <FiCopy />}
                        </button>
                      </div>
                    </div>
                    {editingCitation === key ? (
                      <textarea
                        className="citation-edit-textarea"
                        value={citations[key] || ''}
                        rows={key === 'bibtex' ? 12 : 4}
                        onChange={(e) => setCitations({ ...citations, [key]: e.target.value })}
                      />
                    ) : (
                      <pre className={`citation-text ${key === 'bibtex' ? 'citation-bibtex' : ''}`}>
                        {citations[key]}
                      </pre>
                    )}
                  </div>
                ))}
              </div>

              <div className="citation-modal-footer">
                <button
                  className="admin-btn secondary"
                  onClick={() => setShowCitationReview(false)}
                >
                  Cancel
                </button>
                <button
                  className="admin-btn primary"
                  disabled={saving}
                  onClick={handleConfirmPublish}
                >
                  <FiSend /> {saving ? 'Publishing…' : 'Confirm & Publish'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </GoogleSignInGate>
  );
}
